import type { ReactNode } from 'react'
import type { ContextualMenuItem } from '../ContextualMenu'

/** Color intent of an action button. */
export type ActionButtonTone = 'primary' | 'neutral' | 'danger' | 'success'

/** Fill style of an action button. */
export type ActionButtonVariant = 'solid' | 'outline'

/** Square footprint of an action button. */
export type ActionButtonSize = 'sm' | 'md'

export type ActionButtonProps = {
  /** Icon node rendered inside the button (e.g. a design-system Icon). */
  icon: ReactNode
  /** Accessible label, also used as the native tooltip title. */
  label: string
  onClick?: () => void
  tone?: ActionButtonTone
  variant?: ActionButtonVariant
  size?: ActionButtonSize
  disabled?: boolean
  /** Dark-surface appearance (outline → light border/icon on dark bg). */
  darkMode?: boolean
  className?: string
}

/** Tone and variant overrides for a built-in action button. */
export type ActionButtonAppearance = Pick<ActionButtonProps, 'tone' | 'variant'>

/** A single button inside an {@link ActionButtonsProps.actions} set. */
export type ActionButtonConfig = ActionButtonProps & {
  /** Stable identity for list rendering. */
  id: string
}

export type ActionButtonsProps = {
  /**
   * Fully custom set of buttons. When provided, the convenience props
   * (onView/onEdit/onDelete/...) are ignored.
   */
  actions?: ActionButtonConfig[]
  onView?: () => void
  onEdit?: () => void
  onDelete?: () => void
  showView?: boolean
  showEdit?: boolean
  showDelete?: boolean
  disableEdit?: boolean
  disableDelete?: boolean
  /** Variant override for the default view action (primary tone). */
  viewVariant?: ActionButtonVariant
  /** Variant override for the default edit action (primary tone). */
  editVariant?: ActionButtonVariant
  /**
   * Variant override for the default delete action (danger tone).
   * Defaults to outline (icon on light/transparent surface, not solid fill).
   */
  deleteVariant?: ActionButtonVariant
  /** Create payment (Billing invoice). */
  onPayment?: () => void
  disablePayment?: boolean
  paymentVariant?: ActionButtonVariant
  /** Print (Billing invoice). */
  onPrint?: () => void
  printVariant?: ActionButtonVariant
  /** Apply adjustment / refund (Billing ledger). */
  onRefund?: () => void
  disableRefund?: boolean
  refundVariant?: ActionButtonVariant
  /** Create invoice (Billing ledger). */
  onCreateInvoice?: () => void
  disableCreateInvoice?: boolean
  createInvoiceVariant?: ActionButtonVariant
  /** View payment notes (Billing payment history). */
  onViewNotes?: () => void
  disableViewNotes?: boolean
  viewNotesVariant?: ActionButtonVariant
  /** Rendered between the view and edit buttons (custom middle action). */
  middle?: ReactNode
  /**
   * Overflow items surfaced behind a kebab menu. Accepts ready-made
   * {@link ContextualMenuItem}s or the convenience handlers below.
   */
  moreItems?: ContextualMenuItem[]
  onDuplicate?: () => void
  onPin?: () => void
  isPinned?: boolean
  onPhone?: () => void
  onMail?: () => void
  onMessage?: () => void
  size?: ActionButtonSize
  /** Dark-surface appearance for the set and overflow menu. */
  darkMode?: boolean
  className?: string
  /** Static visual state for the design-system showcase only. */
  previewOpen?: boolean
}

export const ACTION_BUTTON_TONES: ActionButtonTone[] = [
  'primary',
  'neutral',
  'danger',
  'success',
]

export const ACTION_BUTTON_TONE_LABELS: Record<ActionButtonTone, string> = {
  primary: 'Primary',
  neutral: 'Neutral',
  danger: 'Danger',
  success: 'Success',
}

export const ACTION_BUTTON_VARIANTS: ActionButtonVariant[] = [
  'solid',
  'outline',
]

export const ACTION_BUTTON_VARIANT_LABELS: Record<ActionButtonVariant, string> =
  {
    solid: 'Solid',
    outline: 'Outline',
  }

export const ACTION_BUTTON_SIZES: ActionButtonSize[] = ['sm', 'md']

export const ACTION_BUTTON_SIZE_LABELS: Record<ActionButtonSize, string> = {
  sm: 'Small',
  md: 'Medium',
}

export const ACTION_BUTTON_DEFAULT_VIEW_VARIANT: ActionButtonVariant = 'solid'

export const ACTION_BUTTON_DEFAULT_EDIT_VARIANT: ActionButtonVariant = 'outline'

export const ACTION_BUTTON_DEFAULT_DELETE_VARIANT: ActionButtonVariant =
  'outline'

export const ACTION_BUTTON_DEFAULT_PAYMENT_VARIANT: ActionButtonVariant =
  'solid'

export const ACTION_BUTTON_DEFAULT_PRINT_VARIANT: ActionButtonVariant = 'solid'

export const ACTION_BUTTON_DEFAULT_REFUND_VARIANT: ActionButtonVariant =
  'outline'

export const ACTION_BUTTON_DEFAULT_CREATE_INVOICE_VARIANT: ActionButtonVariant =
  'outline'

export const ACTION_BUTTON_DEFAULT_VIEW_NOTES_VARIANT: ActionButtonVariant =
  'outline'
